import React, { useCallback, useEffect, useState } from "react";
import { SelectList } from "../../../shared/inputs/SelectList";
import { Pill } from "../../../shared/text/Pill.styled";
import { useProject } from "../../../context/ProjectContext";
import { useBanner } from "../../../context/BannerContext";
import { useDatasources } from "../../../context/DatasourceContext";
import { useDrawerActions } from "../../../context/DrawerContext";
import { useGcgrLoader } from "../../../hooks/useGcgrLoader";
import { fetchGCGRsByProjectIdUncached } from "../../../../api/gcgrs";
import { GCGR } from "../../../../interfaces/GCGRs";
import { GCGRS_EMPTY, GCGRS_LOAD_ERROR } from "../../../constants";

const GcgrList: React.FC = () => {
  const { selectedProject, selectedGcgr } = useProject();
  const { addDatasource } = useDatasources();
  const { closeDrawer } = useDrawerActions();
  const { loadGcgr } = useGcgrLoader();
  const { showBanner } = useBanner();
  const [gcgrs, setGcgrs] = useState<GCGR[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const projectId = selectedProject?.id;

  useEffect(() => {
    if (!projectId) return;

    let isCurrent = true;

    const loadGcgrs = async () => {
      setLoading(true);
      try {
        const result = await fetchGCGRsByProjectIdUncached(projectId, { activeOnly: false });
        if (!result.success) throw result.error;
        // Actives first, then most recently updated.
        const sorted = [...(result.data ?? [])].sort((a, b) => {
          if (a.active !== b.active) return a.active ? -1 : 1;
          return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
        });
        if (isCurrent) setGcgrs(sorted);
      } catch (error) {
        if (isCurrent) showBanner(GCGRS_LOAD_ERROR, "error");
      } finally {
        if (isCurrent) setLoading(false);
      }
    };

    loadGcgrs();

    return () => {
      isCurrent = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // See EstimatesList: the load decides whether a card is earned, and the loader owns the selection.
  const handleSelectGcgr = useCallback(
    async (gcgr: GCGR) => {
      const { dataLoaded } = await loadGcgr(gcgr);
      if (!dataLoaded) return;
      addDatasource("ediphi", "gcgr");
      closeDrawer();
    },
    [addDatasource, closeDrawer, loadGcgr]
  );

  return (
    <SelectList<GCGR>
      items={gcgrs.map((gcgr) => ({
        id: gcgr.id,
        label: gcgr.name,
        endAdornment: gcgr.active ? <Pill>Active</Pill> : null,
        data: gcgr,
      }))}
      onSelectItem={(gcgr) => void handleSelectGcgr(gcgr)}
      title="GCGRs"
      selectedItemId={selectedGcgr?.id ?? null}
      loading={loading}
      emptyMessage={GCGRS_EMPTY}
    />
  );
};

export default GcgrList;
